// --------------------------------------------------------------
// Created On: 2025-11-05
// Author: Hannah Vaughan
//
// Last Modified: 2025-12-05
// Modified By: Hannah Vaughan
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useRef } from "react";

// Button for importing a CSV file containing devices that
// should have their activation status updated that then gets parsed.
export default function ImportCsvButton(props: Props): Component {
  const uploadRef = useRef<HTMLInputElement>(null);

  // Opens a file dialog to upload a CSV file and processes the file when selected.
  function handleUpload(event: React.ChangeEvent<HTMLInputElement>): void {
    // Check if the file input is empty or if no files were selected.
    // If the file input is empty, show an error message.
    if (event.target.files === null || event.target.files.length === 0) {
      props.onErrorMessageChange("Please select a file to upload.");
      return;
    }

    // Get the first file from the input element.
    const file = event.target.files[0];

    // Check if the file is not null or undefined.
    // If the file is null or undefined, show an error message.
    if (file !== null && file !== undefined) {
      // Check if the file type is CSV.
      // If the file type is not CSV, show an error message.
      // If the file type is CSV, read its contents and process it.
      if (!file.name.endsWith(".csv")) {
        props.onErrorMessageChange("Please select a valid CSV file.");
        return;
      }
      const fileReader = new FileReader();
      fileReader.onload = csvOnLoad;

      // Clear the input value to allow re-uploading the same file.
      event.target.value = "";

      // Read the file as text.
      fileReader.readAsText(file);
    } else {
      props.onErrorMessageChange("File could not be uploaded. Please try again.");
    }
  }

  // This function is called when the CSV file is loaded and the contents are read.
  // It processes the contents of the CSV file and stores the data in an array of objects.
  // The function also formats the headers and retrieves the data rows from the CSV file.
  function csvOnLoad(event: ProgressEvent<FileReader>): void {
    const contents = event.target?.result;
    if (contents !== null && contents !== undefined) {
      // Initialize an empty array to store the devices.
      const uploadedDevices: UploadedDevice[] = [];
      const dataRows: string[][] = [];

      // Create and format the headers and data rows from the CSV file.
      const csvData = JSON.stringify(contents).replace(/\\r|"/g, "").split("\\n");
      csvData.forEach((row) => {
        const dataRow = [...row.split(",")];
        dataRows.push(dataRow);
      });

      // Filter out the headers that are not needed and validate the remaining headers.
      const headers = filterAndValidateHeaders(dataRows[0]);
      if (headers.length === 0) {
        props.onErrorMessageChange("Invalid headers in the CSV file.");
        return;
      }

      // Format the headers and check if they are valid.
      const formattedHeaders: string[] = [];
      for (const header of headers) {
        const formattedHeader = formatHeader(header);
        if (formattedHeader === "") {
          props.onErrorMessageChange(`Invalid header: ${header}`);
          return;
        } else {
          formattedHeaders.push(formattedHeader);
        }
      }

      // Retrieve the data rows from the CSV file, skipping the first row, containing the headers.
      const rows = dataRows.slice(1);
      for (const row of rows) {
        // Check if the row is not empty.
        // Checking for a row length greater than 1 to ensure it contains data.
        // An empty row ( [""] ) would have a length of 1.
        if (row.length > 1) {
          // If the row is not empty, split it into an array of values.
          const singleDataRow = row.filter((column) => column !== undefined && column !== null);

          // Initialize an empty object to store the uploaded part.
          // This object will be used to store the data for each part.
          let uploadedDevice: UploadedDevice = {
            deviceType: "",
            identifier: "",
            activationStatus: "",
          };

          // Get the value for each header.
          formattedHeaders.forEach((header, i) => {
            const values = singleDataRow;
            const value = values[i] === undefined ? "" : values[i];

            // Use the header as the key and assign the value to the uploadedDevice object.
            uploadedDevice = {
              ...uploadedDevice,
              [header]: value,
            };
          });
          if (uploadedDevice.deviceType !== "") {
            // Add the uploaded device to the array.
            uploadedDevices.push(uploadedDevice);
          }
        }
      }

      // Create an array of devices with the additionally needed information for rendering the device list in the form.
      const uploadedDevicesList: Device[] = [];
      for (const device of uploadedDevices) {
        const uploadedDevice: Device = {
          deviceTypeId: getDeviceTypeIdByName(device.deviceType, props.deviceTypes, props.defaultDeviceTypeId),
          identifier: device.identifier,
          activationStatus: device.activationStatus,
        };
        uploadedDevicesList.push(uploadedDevice);
      }

      props.onUploadedDevicesListChange(uploadedDevicesList);
    } else {
      props.onErrorMessageChange("File is empty or could not be read.");
      return;
    }
  }

  // Filters and validates the headers of the CSV file.
  function filterAndValidateHeaders(headers: string[]): string[] {
    const validHeaders = ["device_type", "identifier", "activation_status"];
    const filteredHeaders: string[] = headers.filter((item) => item !== "notes");
    const allHeadersValid: boolean = validHeaders.every((header) => filteredHeaders.includes(header));
    if (allHeadersValid) {
      return filteredHeaders;
    } else {
      return [];
    }
  }

  // Formats the headers of the CSV file.
  function formatHeader(header: string): string {
    switch (header) {
      case "device_type":
        return "deviceType";
      case "identifier":
        return "identifier";
      case "activation_status":
        return "activationStatus";
      default:
        // If the header is not recognized, return an empty string.
        return "";
    }
  }

  // Retrieves the device type ID by name from the deviceTypes array.
  function getDeviceTypeIdByName(name: string, deviceTypes: DeviceType[], defaultDeviceTypeId: number): number {
    const deviceType = deviceTypes.find((deviceType) => deviceType.name.toLowerCase() === name.toLowerCase());
    if (deviceType) {
      return deviceType.deviceTypeId;
    } else {
      return defaultDeviceTypeId;
    }
  }

  return (
    <Fragment>
      <button
        data-test="import-csv-button"
        type="submit"
        className="btn btn-success mx-2"
        onClick={() => uploadRef.current?.click()}
      >
        Import CSV
      </button>
      <input type="file" ref={uploadRef} className="d-none" accept=".csv" onChange={(e) => handleUpload(e)} />
    </Fragment>
  );
}

interface Props {
  defaultDeviceTypeId: number;
  deviceTypes: DeviceType[];
  onUploadedDevicesListChange: (devicesList: Device[]) => void;
  onErrorMessageChange: (message: string) => void;
}

interface DeviceType {
  deviceTypeId: number;
  model: string;
  name: string;
}

interface UploadedDevice {
  deviceType: string;
  identifier: string;
  activationStatus: string;
}

interface Device {
  deviceTypeId: number;
  identifier: string;
  activationStatus: string;
}
